import React, { useCallback, useEffect, useRef, useState } from "react";
import ComboField from "./ComboField";
import { createTimeEntry, getTasks, getOtherTasks } from "../../api";
import {
  asList, openOtherTasks, parsePickerValue, pickerValue, taskPickerOptions,
} from "./taskOptions";
import { formatElapsed, bankableHours, isOvernight } from "./timerMath";
import { TE_STYLES } from "./teStyles";
import { deriveYearEnd } from "../../utils/yearEnd";

const KEY = "te_timer";
const DATE_FMT = { weekday: "short", month: "short", day: "numeric" };
const YEARS = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + 1 - i);

// Same two options, same labels as the grid's Type column.
const TYPES = [
  { value: "BILLABLE", label: "Billable" },
  { value: "NON_BILLABLE", label: "Non-bill" },
];

// Elapsed time is `accumulatedMs` (whole, finished segments) plus, when the
// timer is running, the live segment since `runningSince`. The live part is
// always derived from the clock rather than counted up by the interval, so a
// backgrounded tab whose timers stopped firing still reports the truth.
// `runningSince === null` means paused.
export function elapsedOf(timer, now) {
  if (!timer) return 0;
  const banked = timer.accumulatedMs || 0;
  if (!timer.runningSince) return banked;
  return banked + (now - Date.parse(timer.runningSince));
}

// A running timer is stored rather than held in state, so a reload, a crash or
// a closed tab does not lose the morning.
function readStored() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const s = raw ? JSON.parse(raw) : null;
    if (!s) return null;
    // Timers written before pause existed carry only `startedAt`. Read them as
    // a running timer with nothing banked, so one left over an upgrade is not
    // silently discarded.
    if (s.startedAt && s.accumulatedMs == null && s.runningSince === undefined) {
      const { startedAt, ...rest } = s;
      return { ...rest, accumulatedMs: 0, runningSince: startedAt };
    }
    return (s.runningSince || s.accumulatedMs != null) ? s : null;
  } catch (_) {
    return null;
  }
}

function writeStored(state) {
  try {
    if (state) window.localStorage.setItem(KEY, JSON.stringify(state));
    else window.localStorage.removeItem(KEY);
  } catch (_) {}
}

// `today` and `todayVisible` come from the page: the grid on screen is not
// always the week containing today, and a line dropped onto a date you cannot
// see needs saying out loud.
export default function TimerPanel({ clients, timeCodes, today, todayVisible = true, onAdded }) {
  const [timer, setTimer] = useState(readStored);
  const [now, setNow] = useState(() => Date.now());
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set on mount if we came back to a timer that had been running for hours;
  // nothing can be banked until it is answered.
  // Judged on time actually worked, not wall clock: a timer paused on Friday
  // must not be challenged every Monday morning.
  const [stale, setStale] = useState(() => {
    const s = readStored();
    return s ? isOvernight(elapsedOf(s, Date.now())) : false;
  });
  const tick = useRef(null);
  // Tasks are per-client and fetched on demand, exactly as WeekView does it.
  const [tasks, setTasks] = useState([]);
  // The client's open Other tasks, offered after the regular ones -- the same
  // list, labels and "t:"/"o:" values as the grid (taskOptions.js).
  const [otherTasks, setOtherTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const elapsed = elapsedOf(timer, now);
  const running = !!timer?.runningSince;

  // Same rule as the grid, read the same way: the TIMER naming a task is what
  // locks the year, not the task being findable in the fetched list. The list
  // is empty for the width of every fetch and omits tasks in closed years, and
  // either would otherwise leave an ordinary dropdown here.
  const timerTask = timer?.task
    ? tasks.find((t) => String(t.id) === String(timer.task))
    : null;
  const yearLocked = !!timer?.task;

  useEffect(() => {
    if (!timer || !timer.runningSince) return undefined;
    tick.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick.current);
  }, [timer]);

  const loadTasks = useCallback(async (clientId) => {
    if (!clientId) { setTasks([]); setOtherTasks([]); return; }
    setTasksLoading(true);
    // Its own request and its own failure: losing the Other tasks must never
    // cost the regular list.
    (async () => {
      let others = [];
      try {
        others = openOtherTasks(asList(await getOtherTasks({ client: clientId, horizon: "all" })));
      } catch (_) { /* the regular tasks still stand */ }
      setOtherTasks(others);
    })();
    try {
      // horizon=all so forward-planned work is pickable, matching the grid --
      // logging the first hour is one of the ways a task becomes active.
      // year_open for the same reason the grid passes it: a task whose
      // fiscal year has been closed cannot take time.
      const t = await getTasks({
        client: clientId, bookable: "true", horizon: "all", year_open: "true",
      });
      setTasks(Array.isArray(t) ? t : (t.results || []));
    } catch (_) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, []);

  // A stored timer comes back with a client already on it; its tasks have to be
  // refetched before the Task picker can show what was chosen.
  useEffect(() => { loadTasks(timer?.client); }, [timer?.client, loadTasks]);

  const update = useCallback((patch) => {
    setTimer((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      writeStored(next);
      return next;
    });
  }, []);

  // Picking a client drops the task (it belonged to the old client) and fills
  // the year end from the client's fiscal month end -- a default, not a lock,
  // so a value typed afterwards survives.
  const onClientChange = (value) => {
    const client = (clients || []).find((c) => c.id === Number(value));
    const derived = deriveYearEnd(client, today);
    update({
      client: value, task: null, other_task: null,
      ...(derived ? { year_end: derived } : {}),
    });
  };

  // The task settles the fiscal year, exactly as it does in the grid
  // (WeekView.onChange): today's date cannot tell FY2025 work done after that
  // year ended from FY2027 work. A task with no fiscal_year, and clearing the
  // task, both leave the year as it stands -- and so does an Other task,
  // which carries no fiscal year at all.
  const onTaskChange = (value) => {
    const link = parsePickerValue(value);
    const picked = link.task
      ? tasks.find((t) => String(t.id) === String(link.task))
      : null;
    update({
      ...link,
      ...(picked?.fiscal_year ? { year_end: picked.fiscal_year } : {}),
    });
  };

  const start = () => {
    const next = {
      accumulatedMs: 0, runningSince: new Date().toISOString(),
      client: null, task: null, other_task: null, time_code: "", notes: "", year_end: null,
      billable_code: "BILLABLE",
    };
    writeStored(next);
    setNow(Date.now());
    setTimer(next);
    setBanner(null);
  };

  // Banking the live segment at the moment of the pause is what makes the
  // clock hold still: from here on elapsed is a fixed number.
  const pause = () => {
    if (!timer?.runningSince) return;
    update({ accumulatedMs: elapsedOf(timer, Date.now()), runningSince: null });
    setNow(Date.now());
  };

  const resume = () => {
    if (!timer || timer.runningSince) return;
    update({ runningSince: new Date().toISOString() });
    setNow(Date.now());
  };

  const discard = () => {
    writeStored(null);
    setTimer(null);
    setStale(false);
  };

  const stop = async () => {
    if (!timer || busy) return;
    const hours = bankableHours(elapsedOf(timer, Date.now()));
    setBusy(true);
    try {
      await createTimeEntry({
        date: today,
        client: timer.client || null,
        task: timer.task || null,
        // Absent on a timer stored before Other tasks were pickable.
        other_task: timer.other_task || null,
        time_code: timer.time_code || "",
        // Defaulted rather than assumed: a timer started on a build without
        // this field comes back out of storage with no billable_code at all.
        billable_code: timer.billable_code || "BILLABLE",
        hours,
        notes: timer.notes || "",
        year_end: timer.year_end ? Number(timer.year_end) : null,
      });
      writeStored(null);
      setTimer(null);
      const where = todayVisible
        ? `Added ${hours.toFixed(2)} hrs to today.`
        : `${hours.toFixed(2)} hrs added to ${new Date(`${today}T00:00:00`)
            .toLocaleDateString("en-US", DATE_FMT)}, which is not in view.`;
      setBanner({ type: "ok", msg: where });
      onAdded?.();
    } catch (e) {
      // The timer is deliberately left running: a failed save must not be a
      // silently lost morning.
      setBanner({ type: "error", msg: "Could not add the line. The timer is still running." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="te-timer">
      {/* The panel lives outside WeekView, so it carries the shared sheet
          itself rather than relying on a grid having rendered first. */}
      <style>{TE_STYLES}</style>
      <div className="te-timer-head">
        <span className="te-timer-title">Timer</span>
        <span className={"te-timer-clock" + (timer && !running ? " paused" : "")}
          aria-label="Elapsed">{formatElapsed(elapsed)}</span>
      </div>

      {stale ? (
        <div className="te-timer-stale">
          <div>
            This timer has run for {formatElapsed(elapsed)}. Did you work all of it?
          </div>
          <div className="te-timer-btns">
            <button className="te-timer-go" onClick={() => setStale(false)}>Keep</button>
            <button className="te-timer-x" onClick={discard}>Discard</button>
          </div>
        </div>
      ) : timer ? (
        <>
          <div className="te-timer-row">
            <label>Client</label>
            <ComboField ariaLabel="Timer client" value={timer.client}
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              onChange={onClientChange} />
          </div>
          <div className="te-timer-row">
            <label>Task</label>
            {/* Not disabled when no client is picked, for the same reason the
                grid's Task field is not: with no client the list is simply
                empty, and a field that only becomes enabled on the next render
                is skipped by the very Tab that picked the client. */}
            <ComboField ariaLabel="Timer task" value={pickerValue(timer)}
              placeholder={timer.client && tasksLoading ? "Loading…" : "—"}
              options={taskPickerOptions(tasks, otherTasks)}
              onChange={onTaskChange} />
          </div>
          <div className="te-timer-row">
            <label>Year End</label>
            <ComboField ariaLabel="Timer year end" value={timer.year_end}
              options={YEARS.map((y) => ({ value: y, label: String(y) }))}
              onChange={(v) => update({ year_end: v || null })}
              skipTab
              locked={yearLocked}
              lockedTitle={yearLocked
                ? `From ${timerTask?.name || "the task"} — change the task to change the year`
                : undefined} />
          </div>
          <div className="te-timer-row">
            <label>Type</label>
            <ComboField ariaLabel="Timer type" value={timer.billable_code || "BILLABLE"}
              options={TYPES}
              onChange={(v) => update({ billable_code: v || "BILLABLE" })} />
          </div>
          <div className="te-timer-row">
            <label>Code</label>
            <ComboField ariaLabel="Timer code" value={timer.time_code}
              options={timeCodes.map((t) => ({ value: t.code, label: t.code }))}
              onChange={(v) => update({ time_code: v })} />
          </div>
          <div className="te-timer-row">
            <label>Notes</label>
            <input className="te-in" aria-label="Timer notes" placeholder="What are you doing?"
              value={timer.notes} onChange={(e) => update({ notes: e.target.value })} />
          </div>
          <div className="te-timer-btns">
            <button className="te-timer-go" onClick={stop} disabled={busy}>
              {busy ? "Adding…" : "Stop & add"}
            </button>
            <button className="te-timer-x" onClick={running ? pause : resume} disabled={busy}>
              {running ? "Pause" : "Resume"}
            </button>
            <button className="te-timer-x" onClick={discard} disabled={busy}>Discard</button>
          </div>
        </>
      ) : (
        <button className="te-timer-go" onClick={start}>Start</button>
      )}

      {banner && (
        <div className={banner.type === "ok" ? "te-timer-ok" : "te-timer-err"}>{banner.msg}</div>
      )}
    </div>
  );
}
